import { AdditionalCollaboratorsField } from "back-office/shared/components/collaborators/AdditionalCollaboratorsField";
import { BaseSection } from "components/layout/Section/BaseSection";
import { GenericMessage } from "components/feedback/Message";
import { NsKey } from "enums/NsKey";
import { InputFactory } from "factories/InputFactory";
import { AddressFormFieldNames } from "shared/enums/addressFormFieldNames";
import { useFormikContext } from "formik";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { useCallback } from "react";
import { brokerInputsConfig } from "../form/brokerInputsConfig";
import { brokerEditInputsConfig } from "./brokerEditFormConfig";
import { BrokerFormFieldNames } from "./BrokerFormFieldNames";
import { BrokerFormValues } from "./BrokerFormValues";

import { GerantSection } from 'back-office/shared/components/gerant/GerantSection';
export enum BrokerFormVariant {
  Plateforme,
  Global
}

interface BrokerLayoutProps {
  variant: BrokerFormVariant
  /** Edit mode: no password, cabinet type, manager or additional collaborators (not in the update). */
  isEdit?: boolean
}

export const BrokerLayout: React.FC<BrokerLayoutProps> = ({ variant, isEdit = false }) => {
  const { t } = useDynamicTranslation([NsKey.ActorForm, NsKey.Common]);
  const { values } = useFormikContext<BrokerFormValues>();

  const inputsConfig = isEdit ? brokerEditInputsConfig : brokerInputsConfig;
  const renderField = useCallback((fieldName: keyof BrokerFormValues) => {
    return (
      <InputFactory config={inputsConfig} fieldName={fieldName} />
    );
  }, [inputsConfig]);

  const typeCabinetDescription =
    values.sousNature === 'GRO'
      ? t('ACTOR_FORM.BROKER.TYPES_CABINET.GROSSISTE_DESC')
      : t('ACTOR_FORM.BROKER.TYPES_CABINET.CLASSIQUE_DESC');

  return (
    <div className="flex flex-col gap-4">
      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.GENERAL_INFO')}>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            {renderField(BrokerFormFieldNames.CompanyName)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.Phone)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.ComplaintEmail)}
          </div>
          {!isEdit && (
            <div className="col-span-1">
              {renderField(BrokerFormFieldNames.SousNature)}
              <p className="mt-1 text-xs text-gray-500">{typeCabinetDescription}</p>
            </div>
          )}
        </div>
      </BaseSection>

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.LEGAL_INFO')}>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.RegistrationNumber)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.Capital)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.RcsCity)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.RcsNumber)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.OriasNumber)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.Rcp)}
          </div>
        </div>
      </BaseSection>

      <BaseSection title={t('COMMON.LABELS.COORDINATE')}>
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6">
            {renderField(AddressFormFieldNames.Address)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.AdditionalAddress)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.PostalCode)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.City)}
          </div>
          <div className="col-span-3">
            {renderField(AddressFormFieldNames.Country)}
          </div>
        </div>
      </BaseSection>

      {isEdit && (
        <GenericMessage
          type="warning"
          size="sm"
          message="Modifier l'e-mail ou l'identifiant change la connexion du courtier."
        />
      )}

      <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.USER_INFO')}>
        <div className="grid grid-cols-2 gap-4">
          {
            variant === BrokerFormVariant.Global &&
            <div className="col-span-2">
              {renderField(BrokerFormFieldNames.PlateformeIds)}
            </div>
          }
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.FirstName)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.LastName)}
          </div>
          <div className="col-span-2">
            {renderField(BrokerFormFieldNames.Email)}
          </div>
          <div className="col-span-1">
            {renderField(BrokerFormFieldNames.Username)}
          </div>
          {!isEdit && (
            <div className="col-span-1">
              {renderField(BrokerFormFieldNames.Password)}
            </div>
          )}
        </div>
      </BaseSection>

      {!isEdit && (
        <>
          <GerantSection config={brokerInputsConfig} />

          <BaseSection title={t('ACTOR_FORM.BROKER.SECTIONS.ADDITIONAL_COLLABORATORS')}>
            <AdditionalCollaboratorsField name={BrokerFormFieldNames.Collaborators} />
          </BaseSection>
        </>
      )}

    </div>
  );
};
